import { BrowserRouter, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { AppRoutes } from "./router";
import { I18nextProvider } from "react-i18next";
import i18n from "./i18n";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { MarketAccountProvider } from "./contexts/MarketAccountContext";
import { AppConfigProvider } from "@/contexts/AppConfigContext";
import { NotificationProvider } from "@/contexts/NotificationContext";
import TabBar from "@/components/feature/TabBar";
import SignalToast from "@/components/feature/SignalToast";
import TotpPrompt from "@/components/feature/TotpPrompt";
import { Capacitor } from '@capacitor/core';
import { App as NativeApp } from '@capacitor/app';
import { routeForExternalAppLink, externalAuth } from '@/services/externalAuth';

// Routes that should NOT show the TabBar
const NO_TABBAR_PATHS = ['/auth', '/backtest', '/splash', '/copy-config'];
const TOTP_PROMPT_DISMISSED_PREFIX = 'fm_totp_prompt_dismissed:';

function GlobalTabBar() {
  const location = useLocation();
  const hide = NO_TABBAR_PATHS.some(p => location.pathname.startsWith(p))
    || /^\/agent\/\d+/.test(location.pathname);
  if (hide) return null;
  return <TabBar />;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function ExternalAuthLinkListener() {
  const navigate = useNavigate();
  const lastHandled = useRef('');
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    let active = true;
    const receive = (raw: string) => {
      if (!active) return;
      const link = routeForExternalAppLink(raw);
      if (!link) return;
      if (lastHandled.current === raw) return;
      lastHandled.current = raw;
      void externalAuth.closeBrowser();
      navigate(link.path, { replace: true, state: {
        provider: link.provider, flowId: link.flowId, handoff: link.handoff,
      } });
    };
    const listener = NativeApp.addListener('appUrlOpen', event => receive(event.url));
    void NativeApp.getLaunchUrl().then(result => { if (result?.url) receive(result.url); });
    return () => { active = false; void listener.then(handle => handle.remove()); };
  }, [navigate]);
  return null;
}

/* ── Splash redirect guard ─────────────────────────────────── */
function SplashGuard() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const hasSeen = localStorage.getItem('fm_has_seen_splash');
    if (!hasSeen && location.pathname !== '/splash' && !location.pathname.startsWith('/auth/external/return/')
      && !location.pathname.startsWith('/auth/native/return/')) {
      navigate('/splash', {
        replace: true,
        state: { from: `${location.pathname}${location.search}${location.hash}` },
      });
    }
  }, [navigate, location.hash, location.pathname, location.search]);

  return null;
}

/* ── TOTP 引导弹窗逻辑：只以服务端 two_factor_status 为事实源 ─── */
function TotpPromptWrapper() {
  const { user } = useAuth();
  const [showPrompt, setShowPrompt] = useState(false);

  useEffect(() => {
    // ═══════════════════════════════════════════════════════════
    // 🔒 Hard bypass — 任一条件成立直接 return，绝不弹 TOTP
    // ═══════════════════════════════════════════════════════════

    // 1. Readdy 预览环境
    if (typeof window !== 'undefined') {
      const host = window.location.hostname;
      const path = window.location.pathname;
      if (host.includes('readdy.ai') && path.includes('/preview/')) {
        setShowPrompt(false);
        return;
      }
    }

    // 2. 未登录用户不弹
    if (!user) {
      setShowPrompt(false);
      return;
    }

    // 3. 已开启 TOTP 不弹
    if (user.two_factor_status === 'on') {
      setShowPrompt(false);
      return;
    }

    if (localStorage.getItem(`${TOTP_PROMPT_DISMISSED_PREFIX}${user.user_id}`) === '1') {
      setShowPrompt(false);
      return;
    }

    setShowPrompt(true);
  }, [user]);

  if (!showPrompt) return null;

  return (
    <TotpPrompt
      isOpen={showPrompt}
      onClose={() => {
        if (user) localStorage.setItem(`${TOTP_PROMPT_DISMISSED_PREFIX}${user.user_id}`, '1');
        setShowPrompt(false);
      }}
      onEnable={() => setShowPrompt(false)}
    />
  );
}

function App() {
  return (
    <I18nextProvider i18n={i18n}>
      <BrowserRouter basename={__BASE_PATH__}>
        <AuthProvider>
          <MarketAccountProvider>
            <AppConfigProvider>
              <NotificationProvider>
                <ScrollToTop />
                <ExternalAuthLinkListener />
                <SplashGuard />
                <TotpPromptWrapper />
                <AppRoutes />
                <GlobalTabBar />
                <SignalToast />
              </NotificationProvider>
            </AppConfigProvider>
          </MarketAccountProvider>
        </AuthProvider>
      </BrowserRouter>
    </I18nextProvider>
  );
}

export default App;
